import { useMemo } from 'react';

import dayjs from 'dayjs';

import {
  Bar,
  Box,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  LayoutCard,
  Stack,
  TimeXAxis,
  YAxis,
} from '@cast/design-system';
import type { BillingV1FeatureEntityUsage } from '@cast/types';

import {
  SummaryMetric,
  SummaryMetricsRow,
  SummaryMetricsSkeleton,
} from '../../_components/SummaryMetrics';
import { useResolveThemeColor } from '../_hooks/useResolveThemeColor';
import { CHART_COLORS } from '../constants';
import { buildStackedBarChartData, StackedBarDataPoint } from '../_utils';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Number of individual instance series shown before grouping into "others". */
const TOP_SERIES_COUNT = 5;

/** Sentinel id used for the aggregated "others" bar. */
const OTHERS_SERIES_ID = '__others__';

// ─── Types ───────────────────────────────────────────────────────────────────

type EntitySeries = {
  id: string;
  label: string;
  colorToken: string;
  resolvedColor: string;
  totalUsage: number;
};

type OthersSeries = {
  id: typeof OTHERS_SERIES_ID;
  label: string;
  colorToken: string;
  resolvedColor: string;
  totalUsage: number;
  count: number;
};

type UsageStackedBarChartProps = {
  entities: BillingV1FeatureEntityUsage[];
  isLoading: boolean;
  periodFrom: string;
  periodTo: string;
  /** When non-empty, only the selected entity bars are rendered. */
  selectedEntityIds: string[];
  actions?: React.ReactNode;
};

// ─── Local timezone ──────────────────────────────────────────────────────────

const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ─── Value formatter ─────────────────────────────────────────────────────────

/** 2-decimal formatter used for DBO instance counts (fractional instances). */
const formatDboValue = (value: number): string =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ─── Component ───────────────────────────────────────────────────────────────

export const UsageStackedBarChart = ({
  entities,
  isLoading,
  periodFrom,
  periodTo,
  selectedEntityIds,
  actions,
}: UsageStackedBarChartProps) => {
  const resolveColor = useResolveThemeColor();

  const { series, others, chartData, totalUsage } = useMemo(() => {
    // Sort entities descending by usage so top series get stable palette
    // colors and the "others" group is always the lowest-usage instances.
    const sorted = [...entities].sort(
      (a, b) => (b.usage ?? 0) - (a.usage ?? 0)
    );

    const entitySeries: EntitySeries[] = sorted.map((entity, index) => {
      const id = entity.entityId ?? entity.entityName ?? '';
      const colorToken = CHART_COLORS[index % CHART_COLORS.length];
      return {
        id,
        label: entity.entityName ?? id,
        colorToken,
        resolvedColor: resolveColor(colorToken),
        totalUsage: entity.usage ?? 0,
      };
    });

    // Build base chart data from the API response
    const rawData = buildStackedBarChartData(entities, periodFrom, periodTo);

    // Determine the "rest" entities (beyond the top 5)
    const restSeries = entitySeries.slice(TOP_SERIES_COUNT);

    let othersData: OthersSeries | null = null;
    let chartData: StackedBarDataPoint[];

    if (restSeries.length > 0) {
      const othersColorToken = CHART_COLORS[TOP_SERIES_COUNT];
      othersData = {
        id: OTHERS_SERIES_ID,
        label: `${restSeries.length} others`,
        colorToken: othersColorToken,
        resolvedColor: resolveColor(othersColorToken),
        totalUsage: restSeries.reduce((sum, s) => sum + s.totalUsage, 0),
        count: restSeries.length,
      };

      const restIds = new Set(restSeries.map((s) => s.id));

      // Add the per-day others aggregate into each data point
      chartData = rawData.map((point) => {
        let othersValue = 0;
        for (const id of restIds) {
          const v = point[id];
          if (typeof v === 'number') {
            othersValue += v;
          }
        }
        return {
          ...point,
          [OTHERS_SERIES_ID]: othersValue,
        };
      });
    } else {
      chartData = rawData;
    }

    const total = sorted.reduce((s, e) => s + (e.usage ?? 0), 0);

    return {
      series: entitySeries,
      others: othersData,
      chartData,
      totalUsage: total,
    };
  }, [entities, periodFrom, periodTo, resolveColor]);

  // Top-5 series (for bar rendering when no selection is active)
  const topSeries = series.slice(0, TOP_SERIES_COUNT);

  // Determine which bars to render and which metrics to show in the strip.
  // When a selection is active: show only the selected series (classic parity).
  // When no selection: show top-5 bars + an "others" aggregate bar.
  const visibleSeries =
    selectedEntityIds.length > 0
      ? series.filter((s) => selectedEntityIds.includes(s.id))
      : topSeries;

  // Summary-strip metrics follow the same visibility logic as the bars.
  const stripSeries =
    selectedEntityIds.length > 0
      ? series.filter((s) => selectedEntityIds.includes(s.id))
      : topSeries;

  const today = dayjs().format('YYYY-MM-DD');

  return (
    <LayoutCard
      title="Usage over time"
      headerEndContent={actions}
      testId="dbo-usage-stacked-bar-chart"
      slotProps={{
        title: { variant: 'P18B' },
        header: { py: 12 },
        content: { sx: { pt: 0, overflow: 'visible' } },
      }}
    >
      <Stack gap={12}>
        {/* Summary metrics strip */}
        {isLoading ? (
          <SummaryMetricsSkeleton />
        ) : (
          <SummaryMetricsRow>
            {/* Total usage */}
            <SummaryMetric
              label="Total usage"
              value={totalUsage}
              unit="database instances"
              formatValue={formatDboValue}
            />

            {/* Top-5 (or selected) instance metrics */}
            {stripSeries.map((s) => (
              <SummaryMetric
                key={s.id}
                label={s.label}
                value={s.totalUsage}
                unit="instances"
                color={s.resolvedColor}
                formatValue={formatDboValue}
              />
            ))}

            {/* "N others" aggregate — only when no selection and rest exist */}
            {selectedEntityIds.length === 0 && others !== null && (
              <SummaryMetric
                label={others.label}
                value={others.totalUsage}
                unit="instances"
                color={others.resolvedColor}
                formatValue={formatDboValue}
              />
            )}
          </SummaryMetricsRow>
        )}

        {/* Chart */}
        <Box
          height={230}
          width="100%"
          sx={{ overflow: 'visible' }}
          aria-label={`Daily per-instance usage as stacked bars for ${dayjs(
            periodFrom
          ).format('MMMM YYYY')} (month to date)`}
          role="img"
        >
          <ChartContainer<StackedBarDataPoint>
            data={chartData}
            isLoading={isLoading}
            fallbackChartType="bar"
            barConfig={{ barSize: 20, maxBarSize: 40 }}
            sx={{
              height: '100%',
              overflow: 'visible',
              '& .recharts-tooltip-wrapper': { zIndex: 1300 },
            }}
            // Small left margin so the mirrored y-axis labels are not
            // cramped against the card's left edge.
            margin={{ left: 0, right: 0, top: 12, bottom: 0 }}
            testId="dbo-usage-stacked-bar-chart-plot"
          >
            <ChartGrid hideVertical />

            {visibleSeries.map((s) => (
              <Bar<StackedBarDataPoint>
                key={s.id}
                stackId="instances"
                dataKey={(d: StackedBarDataPoint) => {
                  const v = d[s.id];
                  // Days with no data (incl. future days) render as 0 —
                  // NaN would make recharts render a full-height bar segment.
                  if (typeof v !== 'number') return 0;
                  if (d.day > today) return 0;
                  return v;
                }}
                fill={s.colorToken}
                radius={0}
                isAnimationActive={false}
                tooltipConfig={{
                  displayName: s.label,
                  unit: 'instances',
                }}
              />
            ))}

            {/* "Others" aggregate bar — only rendered when no selection active */}
            {selectedEntityIds.length === 0 && others !== null && (
              <Bar<StackedBarDataPoint>
                key={OTHERS_SERIES_ID}
                stackId="instances"
                dataKey={(d: StackedBarDataPoint) => {
                  const v = d[OTHERS_SERIES_ID];
                  // Days with no data (incl. future days) render as 0 —
                  // NaN would make recharts render a full-height bar segment.
                  if (typeof v !== 'number') return 0;
                  if (d.day > today) return 0;
                  return v;
                }}
                fill={others.colorToken}
                radius={0}
                isAnimationActive={false}
                tooltipConfig={{
                  displayName: others.label,
                  unit: 'instances',
                }}
              />
            )}

            {/* DBO usage is fractional (normalized database instances) — allow
                decimal ticks and let the domain auto-scale to the data. The
                absolute top padding used by CPU-scale charts (16 value units)
                would flatten sub-1 values to a sliver. */}
            <YAxis<StackedBarDataPoint>
              tickCount={5}
              tickFormatter={(v) => `${v}`}
              allowDecimals
            />
            <TimeXAxis<StackedBarDataPoint>
              unixDataKey="chartTimestampUnix"
              timezone={LOCAL_TIMEZONE}
              // Nudge the bars right of the mirrored y-axis labels so the
              // first bar does not overlap them (bar center is inset by the
              // padding, bar is half-width on each side).
              padding={{ left: 48 }}
            />

            <ChartTooltip<StackedBarDataPoint>
              header={(props) => (
                <ChartTooltipTimestampHeader
                  {...props}
                  timezone={LOCAL_TIMEZONE}
                />
              )}
              formatValue={(value) =>
                Number(value).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              }
            />
          </ChartContainer>
        </Box>
      </Stack>
    </LayoutCard>
  );
};

// Export colorMap builder for table synchronization
export const buildEntityColorMap = (
  entities: BillingV1FeatureEntityUsage[],
  resolveColor: (token: string) => string
): Map<string, string> => {
  const sorted = [...entities].sort((a, b) => (b.usage ?? 0) - (a.usage ?? 0));
  return new Map(
    sorted.map((entity, index) => {
      const id = entity.entityId ?? entity.entityName ?? '';
      const colorToken = CHART_COLORS[index % CHART_COLORS.length];
      return [id, resolveColor(colorToken)];
    })
  );
};
